<script setup>
import { computed, onMounted, ref, reactive, watch } from 'vue';
import { useMapGetter, useStore } from 'dashboard/composables/store';
import { useRoute, useRouter } from 'vue-router';
import { useAlert } from 'dashboard/composables';
import AiFocusAgentAPI from 'dashboard/api/aifocus/agent';

import PageLayout from 'dashboard/components-next/aifocus/PageLayout.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import Input from 'dashboard/components-next/input/Input.vue';

const store = useStore();
const route = useRoute();
const router = useRouter();

const agentId = computed(() => Number(route.params.agentId));
const agent = computed(() =>
  store.getters['aifocusAgents/getRecord'](agentId.value)
);

const uiFlags = useMapGetter('aifocusAgents/getUIFlags');
const isFetching = computed(() => uiFlags.value.fetchingItem);
const isUpdating = computed(() => uiFlags.value.isUpdating);

// Available MCP servers from backend
const availableMcpServers = ref([]);
const isLoadingServers = ref(false);

// Agent's configured MCP servers
const mcpServers = ref([]);

// Modal state
const showAddModal = ref(false);
const showCustomModal = ref(false);
const editingServer = ref(null);

// Custom MCP Server Form
const customServerForm = reactive({
  name: '',
  transport: 'stdio',
  command: '',
  args: '',
  env: '',
  url: '',
  enabled: true,
});

const handleBackToSettings = () => {
  router.push({
    name: 'aifocus_agents_edit',
    params: {
      accountId: route.params.accountId,
      agentId: route.params.agentId,
    },
  });
};

const loadAvailableMcpServers = async () => {
  try {
    isLoadingServers.value = true;
    const response = await AiFocusAgentAPI.getMcpServers();
    if (response.data) {
      availableMcpServers.value = response.data.servers || [];
    }
  } catch {
    // Default MCP servers
    availableMcpServers.value = [
      {
        id: 'filesystem',
        name: 'Filesystem',
        description: 'Read and write files from the local filesystem',
        transport: 'stdio',
        command: 'npx',
        args: ['-y', '@modelcontextprotocol/server-filesystem'],
        category: 'storage',
      },
      {
        id: 'postgres',
        name: 'PostgreSQL',
        description: 'Query PostgreSQL databases',
        transport: 'stdio',
        command: 'npx',
        args: ['-y', '@modelcontextprotocol/server-postgres'],
        category: 'database',
      },
      {
        id: 'brave-search',
        name: 'Brave Search',
        description: 'Search the web using Brave Search API',
        transport: 'stdio',
        command: 'npx',
        args: ['-y', '@anthropic/mcp-server-brave-search'],
        category: 'search',
      },
      {
        id: 'github',
        name: 'GitHub',
        description: 'Interact with GitHub repositories',
        transport: 'stdio',
        command: 'npx',
        args: ['-y', '@anthropic/mcp-server-github'],
        category: 'development',
      },
      {
        id: 'slack',
        name: 'Slack',
        description: 'Send and receive Slack messages',
        transport: 'stdio',
        command: 'npx',
        args: ['-y', '@anthropic/mcp-server-slack'],
        category: 'communication',
      },
      {
        id: 'puppeteer',
        name: 'Puppeteer',
        description: 'Web scraping and browser automation',
        transport: 'stdio',
        command: 'npx',
        args: ['-y', '@anthropic/mcp-server-puppeteer'],
        category: 'automation',
      },
    ];
  } finally {
    isLoadingServers.value = false;
  }
};

const updateAgentFromData = agentData => {
  if (!agentData) return;
  mcpServers.value = agentData.mcp_servers || [];
};

onMounted(async () => {
  // Validate agentId before making any API calls
  if (!agentId.value || isNaN(agentId.value)) {
    console.error('mcp/Index.vue: Invalid agentId on mount:', agentId.value);
    return;
  }

  await store.dispatch('aifocusAgents/get');
  if (!agent.value) {
    await store.dispatch('aifocusAgents/show', agentId.value);
  }
  updateAgentFromData(agent.value);
  await loadAvailableMcpServers();
});

// Watch agent changes - use Vue watch to avoid infinite loops
watch(
  agent,
  newAgent => {
    if (newAgent) updateAgentFromData(newAgent);
  },
  { deep: false }
);

const isServerConfigured = serverId => {
  return mcpServers.value.some(s => s.id === serverId || s.name === serverId);
};

const getConfiguredServer = serverId => {
  return mcpServers.value.find(s => s.id === serverId || s.name === serverId);
};

const addServer = async server => {
  // Validate agentId before making API call
  if (!agentId.value || isNaN(agentId.value)) {
    useAlert('Erro: ID do agente invalido');
    return;
  }

  if (isServerConfigured(server.id)) {
    useAlert('Este servidor ja esta configurado');
    return;
  }

  try {
    const newServer = {
      id: server.id,
      name: server.name,
      transport: server.transport,
      command: server.command,
      args: server.args || [],
      env: server.env || {},
      enabled: true,
    };

    const newServers = [...mcpServers.value, newServer];

    await store.dispatch('aifocusAgents/update', {
      id: agentId.value,
      mcp_servers: newServers,
    });

    mcpServers.value = newServers;
    showAddModal.value = false;
    useAlert('Servidor MCP adicionado com sucesso');
  } catch (error) {
    useAlert(error?.message || 'Erro ao adicionar servidor MCP');
  }
};

const toggleServer = async server => {
  // Validate agentId before making API call
  if (!agentId.value || isNaN(agentId.value)) {
    useAlert('Erro: ID do agente invalido');
    return;
  }

  try {
    const updatedServers = mcpServers.value.map(s => {
      if (s.id === server.id || s.name === server.name) {
        return { ...s, enabled: !s.enabled };
      }
      return s;
    });

    await store.dispatch('aifocusAgents/update', {
      id: agentId.value,
      mcp_servers: updatedServers,
    });

    mcpServers.value = updatedServers;
    useAlert(`Servidor ${server.enabled ? 'desativado' : 'ativado'}`);
  } catch (error) {
    useAlert(error?.message || 'Erro ao atualizar servidor');
  }
};

const removeServer = async server => {
  // Validate agentId before making API call
  if (!agentId.value || isNaN(agentId.value)) {
    useAlert('Erro: ID do agente invalido');
    return;
  }

  if (!confirm(`Deseja remover o servidor "${server.name}"?`)) return;

  try {
    const newServers = mcpServers.value.filter(
      s => s.id !== server.id && s.name !== server.name
    );

    await store.dispatch('aifocusAgents/update', {
      id: agentId.value,
      mcp_servers: newServers,
    });

    mcpServers.value = newServers;
    useAlert('Servidor removido');
  } catch (error) {
    useAlert(error?.message || 'Erro ao remover servidor');
  }
};

const openCustomModal = (server = null) => {
  if (server) {
    editingServer.value = server;
    customServerForm.name = server.name;
    customServerForm.transport = server.transport || 'stdio';
    customServerForm.command = server.command || '';
    customServerForm.args = Array.isArray(server.args)
      ? server.args.join(' ')
      : server.args || '';
    customServerForm.env = JSON.stringify(server.env || {}, null, 2);
    customServerForm.url = server.url || '';
    customServerForm.enabled = server.enabled !== false;
  } else {
    editingServer.value = null;
    customServerForm.name = '';
    customServerForm.transport = 'stdio';
    customServerForm.command = '';
    customServerForm.args = '';
    customServerForm.env = '{}';
    customServerForm.url = '';
    customServerForm.enabled = true;
  }
  showCustomModal.value = true;
};

const saveCustomServer = async () => {
  // Validate agentId before making API call
  if (!agentId.value || isNaN(agentId.value)) {
    useAlert('Erro: ID do agente invalido');
    return;
  }

  try {
    let env = {};
    try {
      env = JSON.parse(customServerForm.env || '{}');
    } catch {
      useAlert('Variaveis de ambiente deve ser um JSON valido');
      return;
    }

    const serverData = {
      id: customServerForm.name.toLowerCase().replace(/\s+/g, '-'),
      name: customServerForm.name,
      transport: customServerForm.transport,
      command: customServerForm.command,
      args: customServerForm.args.split(' ').filter(a => a),
      env,
      url: customServerForm.url,
      enabled: customServerForm.enabled,
      custom: true,
    };

    let newServers;

    if (editingServer.value) {
      newServers = mcpServers.value.map(s => {
        if (
          s.id === editingServer.value.id ||
          s.name === editingServer.value.name
        ) {
          return serverData;
        }
        return s;
      });
    } else {
      newServers = [...mcpServers.value, serverData];
    }

    await store.dispatch('aifocusAgents/update', {
      id: agentId.value,
      mcp_servers: newServers,
    });

    mcpServers.value = newServers;
    showCustomModal.value = false;
    useAlert('Servidor MCP salvo com sucesso');
  } catch (error) {
    useAlert(error?.message || 'Erro ao salvar servidor MCP');
  }
};

const getCategoryIcon = category => {
  const icons = {
    storage: 'i-lucide-hard-drive',
    database: 'i-lucide-database',
    search: 'i-lucide-search',
    development: 'i-lucide-github',
    communication: 'i-lucide-message-square',
    automation: 'i-lucide-bot',
  };
  return icons[category] || 'i-lucide-plug';
};

const getTransportBadge = transport => {
  const badges = {
    stdio: { label: 'STDIO', class: 'bg-green-100 text-green-700' },
    sse: { label: 'SSE', class: 'bg-blue-100 text-blue-700' },
    websocket: { label: 'WebSocket', class: 'bg-purple-100 text-purple-700' },
  };
  return (
    badges[transport] || {
      label: transport,
      class: 'bg-gray-100 text-gray-700',
    }
  );
};
</script>

<template>
  <PageLayout
    :is-fetching="isFetching || isLoadingServers"
    :show-pagination-footer="false"
    :show-know-more="false"
  >
    <template #header>
      <div class="flex items-center justify-between w-full px-6 py-4">
        <div class="flex items-center gap-4">
          <Button
            icon="i-lucide-arrow-left"
            variant="ghost"
            size="sm"
            @click="handleBackToSettings"
          />
          <div>
            <h2 class="text-lg font-semibold text-n-slate-12">
              Servidores MCP
            </h2>
            <p class="text-sm text-n-slate-11">
              Configure servidores MCP (Model Context Protocol) para estender as
              capacidades do agente.
            </p>
          </div>
        </div>
        <div class="flex items-center gap-2">
          <Button
            label="Servidor Personalizado"
            icon="i-lucide-plus"
            variant="secondary"
            @click="openCustomModal()"
          />
          <Button
            label="Adicionar Servidor"
            icon="i-lucide-plug"
            @click="showAddModal = true"
          />
        </div>
      </div>
    </template>

    <template #body>
      <div class="px-6 pb-6">
        <!-- Agent Info -->
        <div
          v-if="agent"
          class="mb-6 p-4 bg-n-alpha-1 border border-n-weak rounded-xl"
        >
          <div class="flex items-center gap-3">
            <div
              class="w-10 h-10 rounded-full bg-n-alpha-2 flex items-center justify-center"
            >
              <span class="i-lucide-bot text-xl text-n-slate-11" />
            </div>
            <div>
              <p class="font-medium text-n-slate-12">{{ agent.name }}</p>
              <p class="text-xs text-n-slate-10">
                {{ mcpServers.filter(s => s.enabled).length }} servidores MCP
                ativos
              </p>
            </div>
          </div>
        </div>

        <!-- Configured MCP Servers -->
        <div class="mb-8">
          <h3
            class="text-md font-semibold text-n-slate-12 mb-4 flex items-center gap-2"
          >
            <span class="i-lucide-server text-n-slate-11" />
            Servidores Configurados
          </h3>

          <div
            v-if="mcpServers.length === 0"
            class="flex flex-col items-center justify-center py-12 text-center bg-n-alpha-1 border border-dashed border-n-weak rounded-xl"
          >
            <div
              class="w-12 h-12 mb-3 rounded-full bg-n-alpha-2 flex items-center justify-center"
            >
              <span class="i-lucide-plug text-2xl text-n-slate-10" />
            </div>
            <h4 class="text-md font-medium text-n-slate-12 mb-1">
              Nenhum servidor MCP configurado
            </h4>
            <p class="text-sm text-n-slate-11 mb-4 max-w-md">
              Adicione servidores MCP para estender as capacidades do seu
              agente.
            </p>
            <Button
              label="Adicionar Servidor"
              icon="i-lucide-plus"
              variant="secondary"
              @click="showAddModal = true"
            />
          </div>

          <div v-else class="grid gap-3">
            <div
              v-for="server in mcpServers"
              :key="server.id || server.name"
              class="flex items-center justify-between p-4 bg-n-solid-1 border border-n-weak rounded-xl"
              :class="{ 'opacity-50': !server.enabled }"
            >
              <div class="flex items-center gap-3">
                <div
                  class="w-10 h-10 rounded-lg flex items-center justify-center"
                  :class="server.enabled ? 'bg-green-100' : 'bg-n-alpha-2'"
                >
                  <span
                    class="text-xl"
                    :class="
                      server.enabled
                        ? 'text-green-600 i-lucide-plug-zap'
                        : 'text-n-slate-10 i-lucide-plug'
                    "
                  />
                </div>
                <div>
                  <div class="flex items-center gap-2">
                    <p class="font-medium text-n-slate-12">{{ server.name }}</p>
                    <span
                      class="px-2 py-0.5 text-xs font-medium rounded"
                      :class="getTransportBadge(server.transport).class"
                    >
                      {{ getTransportBadge(server.transport).label }}
                    </span>
                    <span
                      v-if="server.custom"
                      class="px-2 py-0.5 text-xs font-medium rounded bg-purple-100 text-purple-700"
                    >
                      Custom
                    </span>
                  </div>
                  <p class="text-xs text-n-slate-10 font-mono">
                    {{ server.command }} {{ server.args?.join(' ') || '' }}
                  </p>
                </div>
              </div>
              <div class="flex items-center gap-2">
                <label class="relative inline-flex items-center cursor-pointer">
                  <input
                    type="checkbox"
                    :checked="server.enabled"
                    class="sr-only peer"
                    @change="toggleServer(server)"
                  />
                  <div
                    class="w-11 h-6 bg-n-alpha-3 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-n-brand/20 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-n-brand"
                  />
                </label>
                <Button
                  icon="i-lucide-settings"
                  variant="ghost"
                  size="sm"
                  @click="openCustomModal(server)"
                />
                <Button
                  icon="i-lucide-trash-2"
                  variant="ghost"
                  size="sm"
                  color="ruby"
                  @click="removeServer(server)"
                />
              </div>
            </div>
          </div>
        </div>

        <!-- Info Card -->
        <div class="p-4 bg-purple-50 border border-purple-200 rounded-xl">
          <div class="flex gap-3">
            <span class="i-lucide-info text-purple-600 text-xl flex-shrink-0" />
            <div>
              <h4 class="font-medium text-purple-900 mb-1">O que e MCP?</h4>
              <p class="text-sm text-purple-700">
                O Model Context Protocol (MCP) e um padrao aberto que permite
                conectar agentes de IA a diferentes fontes de dados e
                ferramentas. Com MCP, seu agente pode acessar bancos de dados,
                arquivos, APIs e muito mais de forma segura e padronizada.
              </p>
            </div>
          </div>
        </div>
      </div>

      <!-- Add Server Modal -->
      <div
        v-if="showAddModal"
        class="fixed inset-0 z-50 flex items-center justify-center bg-black/50"
        @click.self="showAddModal = false"
      >
        <div
          class="bg-n-solid-2 rounded-2xl shadow-2xl w-full max-w-2xl mx-4 max-h-[90vh] overflow-y-auto"
        >
          <div
            class="px-6 py-4 border-b border-n-weak sticky top-0 bg-n-solid-2 z-10"
          >
            <h3 class="text-lg font-semibold text-n-slate-12">
              Adicionar Servidor MCP
            </h3>
            <p class="text-sm text-n-slate-11">
              Selecione um servidor MCP para adicionar ao seu agente.
            </p>
          </div>

          <div class="p-6">
            <div class="grid gap-3">
              <button
                v-for="server in availableMcpServers"
                :key="server.id"
                class="flex items-center gap-3 p-4 rounded-xl border text-left w-full transition-colors"
                :class="
                  isServerConfigured(server.id)
                    ? 'border-green-300 bg-green-50 cursor-not-allowed'
                    : 'border-n-weak hover:border-n-brand hover:bg-n-alpha-1'
                "
                :disabled="isServerConfigured(server.id)"
                @click="addServer(server)"
              >
                <div
                  class="w-12 h-12 rounded-lg flex items-center justify-center"
                  :class="
                    isServerConfigured(server.id)
                      ? 'bg-green-100'
                      : 'bg-n-alpha-2'
                  "
                >
                  <span
                    class="text-2xl"
                    :class="[
                      getCategoryIcon(server.category),
                      isServerConfigured(server.id)
                        ? 'text-green-600'
                        : 'text-n-slate-11',
                    ]"
                  />
                </div>
                <div class="flex-1">
                  <div class="flex items-center gap-2">
                    <p class="font-medium text-n-slate-12">{{ server.name }}</p>
                    <span
                      v-if="isServerConfigured(server.id)"
                      class="px-2 py-0.5 text-xs font-medium rounded bg-green-100 text-green-700"
                    >
                      Configurado
                    </span>
                  </div>
                  <p class="text-sm text-n-slate-10">
                    {{ server.description }}
                  </p>
                </div>
                <span
                  v-if="!isServerConfigured(server.id)"
                  class="i-lucide-plus text-n-slate-10"
                />
                <span v-else class="i-lucide-check text-green-600" />
              </button>
            </div>
          </div>

          <div class="px-6 py-4 border-t border-n-weak flex justify-end">
            <Button
              label="Fechar"
              variant="secondary"
              @click="showAddModal = false"
            />
          </div>
        </div>
      </div>

      <!-- Custom Server Modal -->
      <div
        v-if="showCustomModal"
        class="fixed inset-0 z-50 flex items-center justify-center bg-black/50"
        @click.self="showCustomModal = false"
      >
        <div
          class="bg-n-solid-2 rounded-2xl shadow-2xl w-full max-w-xl mx-4 max-h-[90vh] overflow-y-auto"
        >
          <div
            class="px-6 py-4 border-b border-n-weak sticky top-0 bg-n-solid-2 z-10"
          >
            <h3 class="text-lg font-semibold text-n-slate-12">
              {{ editingServer ? 'Editar' : 'Adicionar' }} Servidor MCP
            </h3>
            <p class="text-sm text-n-slate-11">
              Configure um servidor MCP personalizado.
            </p>
          </div>

          <div class="p-6 space-y-4">
            <Input
              v-model="customServerForm.name"
              label="Nome do Servidor"
              placeholder="My Custom Server"
            />

            <div class="flex flex-col gap-2">
              <label class="text-sm font-medium text-n-slate-12">
                Tipo de Transporte
              </label>
              <select
                v-model="customServerForm.transport"
                class="w-full px-3 py-2 bg-n-alpha-1 border border-n-weak rounded-lg text-n-slate-12"
              >
                <option value="stdio">STDIO (Local)</option>
                <option value="sse">SSE (HTTP)</option>
                <option value="websocket">WebSocket</option>
              </select>
            </div>

            <template v-if="customServerForm.transport === 'stdio'">
              <Input
                v-model="customServerForm.command"
                label="Comando"
                placeholder="npx"
              />

              <Input
                v-model="customServerForm.args"
                label="Argumentos (separados por espaco)"
                placeholder="-y @modelcontextprotocol/server-filesystem /path"
              />
            </template>

            <template v-else>
              <Input
                v-model="customServerForm.url"
                label="URL do Servidor"
                placeholder="http://localhost:3001/sse"
              />
            </template>

            <div class="flex flex-col gap-2">
              <label class="text-sm font-medium text-n-slate-12">
                Variaveis de Ambiente (JSON)
              </label>
              <textarea
                v-model="customServerForm.env"
                rows="3"
                class="w-full px-3 py-2 bg-n-alpha-1 border border-n-weak rounded-lg text-n-slate-12 font-mono text-sm"
                placeholder='{"API_KEY": "your-key"}'
              />
            </div>

            <label class="flex items-center gap-2 text-n-slate-12">
              <input
                v-model="customServerForm.enabled"
                type="checkbox"
                class="w-4 h-4"
              />
              Servidor habilitado
            </label>
          </div>

          <div
            class="px-6 py-4 border-t border-n-weak flex justify-end gap-3 sticky bottom-0 bg-n-solid-2"
          >
            <Button
              label="Cancelar"
              variant="secondary"
              @click="showCustomModal = false"
            />
            <Button
              label="Salvar"
              :is-loading="isUpdating"
              @click="saveCustomServer"
            />
          </div>
        </div>
      </div>
    </template>
  </PageLayout>
</template>
